import { useState } from "react";
import { ScrollRestoration, useLocation } from "react-router-dom";

import { useMedplum, useMedplumProfile } from "@medplum/react";

import { AppShell } from "@mantine/core";

import { ErrorBoundary } from "@sentry/react";

import OrganizationsProvider from "@/components/OrganizationsProvider";
import { BackgroundContext } from "@/contexts/background";
import { NavbarContext } from "@/contexts/navbar";
import ErrorPage from "@/ErrorPage";
import { useSessionManager } from "@/hooks/useSessionManager";
import {
  handleClientApplicationRestriction,
  isClientApplicationAllowedRoute,
  isExpiredSession,
  isRestrictedClientApplication,
  setNextRedirectUrl,
} from "@/utils/medplum";

import { Loading } from "../components/Loading";
import { ProfileProvider } from "../components/ProfileProvider";

import Header from "./components/Header";
import MobileHeader from "./components/MobileHeader";
import NavBar from "./components/NavBar";
import { HEADER_HEIGHT } from "./constants/header";
import { NAVBAR_COLLAPSED_WIDTH, NAVBAR_EXPANDED_WIDTH } from "./constants/navbar";
import { useNavbarCollapse } from "./hooks/useNavbarCollapse";

export default function CustomAppShell({ children }: { children: React.ReactNode }): JSX.Element {
  const { pathname } = useLocation();
  const medplum = useMedplum();
  const userProfile = useMedplumProfile();
  const [background, setBackground] = useState<string>("cream.0");
  const { collapsed, hasPhoneNavJustClosed, isPhoneNavOpen, navbarRef, toggleCollapsed } =
    useNavbarCollapse();

  const isAllowedRoute = isClientApplicationAllowedRoute(pathname);

  // Set up periodic token refresh for authenticated Patient/Practitioner users
  // This prevents session expiration during long forms or note-taking sessions
  // The hook internally checks authentication, so it's safe to call unconditionally
  // ClientApplication users are excluded via isRestrictedClientApplication check below
  useSessionManager(medplum);

  // Don't show AppShell for unauthenticated routes
  if (pathname === "/" || pathname === "/oauth" || isAllowedRoute) {
    return <>{children}</>;
  }

  // ClientApplication users should not access the main application shell
  // Redirect them to the sign-in page to prevent unauthorized access
  if (isRestrictedClientApplication(userProfile)) {
    handleClientApplicationRestriction(medplum);
    return <Loading />;
  }

  if (isExpiredSession(medplum)) {
    medplum.signOut();
    setNextRedirectUrl();
    return <Loading />;
  }

  return (
    <OrganizationsProvider>
      <NavbarContext.Provider value={{ collapsed, hasPhoneNavJustClosed, toggleCollapsed }}>
        <AppShell
          layout="alt"
          header={{ height: HEADER_HEIGHT, offset: true }}
          navbar={{
            width: collapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_EXPANDED_WIDTH,
            collapsed: {
              mobile: !isPhoneNavOpen,
              desktop: false,
            },
            breakpoint: "sm",
          }}
        >
          <AppShell.Header
            styles={{
              header: {
                borderBottomColor: "var(--mantine-color-cream-4)",
              },
            }}
          >
            <Header />
          </AppShell.Header>
          <AppShell.Navbar
            ref={navbarRef}
            styles={{
              navbar: {
                borderRightColor: "var(--mantine-color-cream-4)",
              },
            }}
          >
            <MobileHeader />
            <NavBar />
          </AppShell.Navbar>
          <BackgroundContext.Provider value={{ background, setBackground }}>
            <AppShell.Main bg={background}>
              <ErrorBoundary fallback={<ErrorPage />}>
                <ProfileProvider>{children}</ProfileProvider>
              </ErrorBoundary>
            </AppShell.Main>
          </BackgroundContext.Provider>
          <ScrollRestoration />
        </AppShell>
      </NavbarContext.Provider>
    </OrganizationsProvider>
  );
}
